import { useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import classNames from 'classnames';
import Typography from '@material-ui/core/Typography';
import ConditionalTooltip from '../../conditionalTooltip';
import { ellipsis } from '../../../../constants/helperCssRules';
import { getColumnWidth, getAlignItem } from '../../../../constants/gridTable';
import { isEllipsisActive } from '../../../../utility/helpers';

const useStyles = makeStyles(({ palette: { primary }, spacing }) => ({
  root: {
    display: 'grid',
    alignContent: 'center',
    backgroundColor: primary.bluish9,
    boxSizing: 'border-box',
    height: '100%',
    paddingLeft: spacing(2),
    paddingRight: spacing(2),
    borderRadius: 8,
    columnGap: spacing(2),
  },
  rootLink: {
    textDecoration: 'none',
    transition: 'background-color .3s ease',
    '&:hover': {
      backgroundColor: primary.bluish8,
    },
  },
  rowCell: {
    alignSelf: 'center',
    boxSizing: 'border-box',
    color: primary.bluish1,
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    height: '100%',
    ...ellipsis(),
  },
  rowCellLink: {
    cursor: 'pointer',
  },
  cellValue: {
    ...ellipsis(),
  },
  highlightedText: {
    fontFamily: 'ProximaNova-Bold',
  },
}));

const CardsTableRow = ({ headers, row, hasDetailsUrl, prepareDetailsUrl }) => {
  const classes = useStyles();
  const cellsTooltipRef = useRef({});
  const [cellsWithTooltip, setCellsWithTooltip] = useState({});

  useEffect(() => {
    const cellWithTooltip = {};

    Object.keys(cellsTooltipRef.current).forEach(cellKey => {
      cellWithTooltip[cellKey] = isEllipsisActive(
        cellsTooltipRef.current[cellKey],
        false,
        true
      );
    });

    setCellsWithTooltip(cellWithTooltip);
  }, [row]);

  const handleActionCellClick = isHeaderAction => e => {
    if (isHeaderAction) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  const getCellRef = (element, rowKey, hasTooltip) => {
    if (hasTooltip) {
      cellsTooltipRef.current[rowKey] = element;
    }
  };

  const renderCellValue = (cellValue, header, isHighlighted) => (
    <ConditionalTooltip
      addTooltip={
        header.hasConditionalTooltip && cellsWithTooltip?.[header.rowKey]
      }
      message={cellValue || ''}
    >
      <Typography
        component="div"
        ref={ref =>
          getCellRef(ref, header.rowKey, header.hasConditionalTooltip)
        }
        className={classNames(
          'row-cell-value',
          classes.cellValue,
          {
            [classes.highlightedText]: isHighlighted,
          },
          header.cellValueClass
        )}
      >
        {cellValue}
      </Typography>
    </ConditionalTooltip>
  );

  const renderCell = header => (
    <div
      id={header.rowKey}
      key={`row_cell_${row.id}_column_${header.id}`}
      className={classNames(classes.rowCell, 'row-cell', header.rowCellClass, {
        [classes.rowCellLink]: hasDetailsUrl || header.isHeaderAction,
      })}
      style={getAlignItem(header)}
      onClick={handleActionCellClick(header.isHeaderAction)}
    >
      {renderCellValue(
        row[header.rowKey],
        header,
        header.isHighlighted || row.isHighlighted
      )}
    </div>
  );

  const rowClassName = classNames(classes.root, {
    [classes.rootLink]: hasDetailsUrl,
  });
  const rowStyle = getColumnWidth(headers);
  const cells = headers.map(renderCell);

  if (hasDetailsUrl) {
    return (
      <Link
        id={row.id}
        className={rowClassName}
        style={rowStyle}
        to={prepareDetailsUrl(row.id)}
      >
        {cells}
      </Link>
    );
  }

  return (
    <div id={row.id} className={rowClassName} style={rowStyle}>
      {cells}
    </div>
  );
};

CardsTableRow.propTypes = {
  headers: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
      rowKey: PropTypes.string.isRequired,
      align: PropTypes.string,
      isHighlighted: PropTypes.bool,
      minWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      maxWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      rowCellClass: PropTypes.string,
      hasConditionalTooltip: PropTypes.bool,
      isHeaderAction: PropTypes.bool,
      cellValueClass: PropTypes.string,
    })
  ).isRequired,
  row: PropTypes.object.isRequired,
  hasDetailsUrl: PropTypes.bool.isRequired,
  prepareDetailsUrl: PropTypes.func.isRequired,
};

export default CardsTableRow;
